import { useEffect } from 'react';
import {
	rezaOverviewDownloadQuery,
	rezaOverviewSendByDataboxQuery,
	useAxiosInstance,
	usePoMutation,
	useQueryDownload,
} from '@gov-nx/api';
import { useMessageEvents } from '@gov-nx/core/events';
import {
	type FormDefinition2,
	getAllDataboxTypes,
	useFormBuilder,
} from '@gov-nx/core/service';
import { useDataBoxStoreLists } from '@gov-nx/store';
import type { FormCheckboxProps, DataBoxFormData } from '@gov-nx/ui/types';
import {
	type DataBoxFields,
	getCheckboxDefinition,
	useDataboxDefinition,
} from '@gov-nx/utils/common';
import { useRezaLocale } from './useRezaLocale';

interface Fields extends DataBoxFields {
	sendByDatabox: FormCheckboxProps;
}

interface OverViewFormData extends DataBoxFormData {
	sendByDatabox: boolean;
}

export interface UseOverviewForm {
	formDefinition: FormDefinition2<OverViewFormData, Fields>;
	isSentByDatabox: boolean;
	hasDataBoxes: boolean;
	isLoading: boolean;
	formId: string;
	strings: {
		title: string;
		submitButton: string;
	};
	onSubmit: () => void;
}

const OVERVIEW_FORM_ID = 'OverviewForm';

export const useOverviewForm = (rezaCode?: string): UseOverviewForm => {
	const { t } = useRezaLocale();
	const { toastMessageError, toastMessageSuccess } = useMessageEvents();
	const { axiosInstance } = useAxiosInstance();
	const { getConnectedDataBoxesByTypes } = useDataBoxStoreLists();
	const hasDataBoxes =
		getConnectedDataBoxesByTypes(getAllDataboxTypes()).length > 0;
	const downloadOverview = useQueryDownload(rezaOverviewDownloadQuery, {
		onDownloadSuccess: () => formDefinition.formReset(),
	});
	const sendByDataboxMutation = usePoMutation({
		mutationFn: rezaOverviewSendByDataboxQuery,
		onSuccess: () => {
			toastMessageSuccess(t('overview.form.sendByDatabox.uspesne-odeslano'));
			formDefinition.formReset();
		},
		onError: () => toastMessageError(),
	});
	const isLoading =
		downloadOverview.isDownloading || sendByDataboxMutation.isLoading;

	const sendByDatabox = getCheckboxDefinition({
		label: t('overview.form.sendByDatabox.label'),
		name: 'sendByDatabox',
		isDisabled: isLoading,
	});

	const databox = useDataboxDefinition({
		label: t('overview.form.databox.label'),
		isDisabled: isLoading,
		types: getAllDataboxTypes(),
	});

	const formDefinition = useFormBuilder<OverViewFormData, Fields>([
		sendByDatabox,
		...databox,
	]);

	const isSentByDatabox = formDefinition.formMethods.watch('sendByDatabox');

	useEffect(() => {
		if (!hasDataBoxes) {
			formDefinition.formMethods.setValue('sendByDatabox', false);
			formDefinition.formMethods.setValue('datovaSchrankaId', '');
		}
	}, [formDefinition.formMethods, hasDataBoxes]);

	const onSubmit = formDefinition.formMethods.handleSubmit((formData) => {
		const headers = rezaCode ? { 'x-okz': rezaCode } : undefined;

		if (hasDataBoxes && isSentByDatabox) {
			sendByDataboxMutation.mutate({
				axiosInstance,
				headers,
				body: {
					typ: 'Vse',
					odesilatelDSId: formData.datovaSchrankaId,
					naseCisloJednaci: formData.naseCisloJednaci,
					naseSpisovaZnacka: formData.naseSpisovaZnacka,
				},
			});
		} else {
			downloadOverview.download({
				axiosInstance,
				headers,
				body: { typ: 'Vse' },
			});
		}
	});

	return {
		formDefinition,
		isSentByDatabox,
		hasDataBoxes,
		isLoading,
		formId: OVERVIEW_FORM_ID,
		strings: {
			title: t('overview.title'),
			submitButton: t('overview.ziskat'),
		},
		onSubmit,
	};
};
